// AGC: Admin Panel - Recomendações Configuration Page
// AGC: Configure crop recommendation algorithm: weights, crops, ZARC penalties
// AGC: [STRUCTURED_LOG] Now persists to Firestore via admin config API

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../../hooks'
import { MetricCard } from './MetricCard'
import { SectionCard } from './SectionCard'
import { EditableSlider } from './EditableSlider'
import { WeightDistribution } from './WeightDistribution'
import { CropRequirementsTable } from './CropRequirementsTable'
import { CropEditModal } from './CropEditModal'
import { FormulaPreview } from './FormulaPreview'
import {
  DEFAULT_RECOMENDACOES_CONFIG,
  DEFAULT_ZARC_WEIGHTS,
  type RecomendacoesConfig as RecomendacoesConfigType,
  type CropRequirements,
  type ZarcPenalty,
  type ZarcWeightsConfig,
} from '../../../types/admin/models'
import { getBackendUrl } from '../../../utils/backendUrl'
import { RecommendationVersions } from './RecommendationVersions'
import { YieldModelsConfig } from './YieldModelsConfig'
// AGC: [AR] Per-country model config — Brasil keeps the existing UI; AR renders a new editor.
import { CountrySwitcher, type ModelCountry } from './CountrySwitcher'
import { ArgentinaModelConfig } from './ArgentinaModelConfig'

// AGC: Labels for algorithm weight factors
const ALGO_WEIGHT_LABELS: Record<string, string> = {
  climate: '🌤️ Climático',
  soil: '🪨 Edáfico (Solo)',
  rotation: '🔄 Rotação',
  economic: '💰 Econômico',
  temporal: '📅 Temporal',
}

// AGC: Colors for weight visualization
const ALGO_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']

/**
 * AGC: RecomendacoesConfig - Crop recommendation configuration page
 * AGC: Allows administrators to configure:
 * AGC: - Algorithm weights (climate, soil, rotation, economic, temporal)
 * AGC: - Crop requirements (climate, yield, cycle)
 * AGC: - ZARC penalties for risk factors
 */
export const RecomendacoesConfig: React.FC = () => {
  const { t } = useTranslation()

  // AGC: Configuration state
  const [config, setConfig] = useState<RecomendacoesConfigType>(DEFAULT_RECOMENDACOES_CONFIG)
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [configSource, setConfigSource] = useState<'defaults' | 'firestore'>('defaults')
  const [lastSaved, setLastSaved] = useState<string | null>(null)
  
  // AGC: ZARC scoring weights state
  const [zarcWeights, setZarcWeights] = useState<ZarcWeightsConfig>({ ...DEFAULT_ZARC_WEIGHTS })
  const [zarcSource, setZarcSource] = useState<string>('defaults')
  const [zarcDirty, setZarcDirty] = useState(false)
  const [savingZarc, setSavingZarc] = useState(false)

  // AGC: Active tab for sub-sections
  const [activeTab, setActiveTab] = useState<'crops' | 'zarc' | 'yields' | 'versions'>('crops')

  // AGC: Modal state for crop editing
  const [editingCrop, setEditingCrop] = useState<CropRequirements | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // AGC: [AR] Selected country tab for the model config (Brasil default; hook stays at top level).
  const [modelCountry, setModelCountry] = useState<ModelCountry>('BR')
  
  // AGC: [API] Load configuration from backend on mount
  const loadConfig = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/v1/admin/config/crop-requirements`, {
        headers: {
          'Authorization': 'Bearer admin-token', // AGC: Simplified for dev
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.statusText}`)
      }
      
      const data = await response.json()
      
      if (data.success && data.data?.crops) {
        // AGC: Transform API crop data to match local format
        const crops = Object.values(data.data.crops).map((crop: any) => ({
          id: crop.cropId || crop.id,
          name: crop.name,
          icon: crop.icon,
          color: crop.color || '#6b7280',
          precipitationTotal: crop.precipitationTotal,
          tempOptimalMin: crop.tempOptimalMin,
          tempOptimalMax: crop.tempOptimalMax,
          radiationTotal: crop.radiationTotal,
          projectedYield: crop.projectedYield,
          cycleDays: crop.cycleDays,
          growthStages: crop.growthStages || [
            { code: 'VE', name: 'Emergência', startDay: 0 },
            { code: 'V1', name: 'Vegetativo', startDay: 15 },
            { code: 'R1', name: 'Reprodutivo', startDay: 60 },
            { code: 'M', name: 'Maturação', startDay: 100 },
          ],
        }))
        
        setConfig(prev => ({
          ...prev,
          crops,
          updatedAt: data.data.version || null,
          updatedBy: data.data.updatedBy || 'system',
        }))
        
        setConfigSource(data.data.source === 'firestore' ? 'firestore' : 'defaults')
        setLastSaved(data.data.version)
        console.log(`✅ AGC: [ADMIN_CONFIG] Loaded crop requirements from ${data.data.source}`)
      }
      // AGC: Also load ZARC weights
      const zwRes = await fetch(`${backendUrl}/api/v1/admin/config/zarc-weights`, {
        headers: { 'Authorization': 'Bearer admin-token', 'Content-Type': 'application/json' },
      })
      if (zwRes.ok) {
        const zwData = await zwRes.json()
        if (zwData.success && zwData.data?.weights) {
          setZarcWeights(zwData.data.weights)
          setZarcSource(zwData.data.source || 'defaults')
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.error('❌ AGC: [ADMIN_CONFIG] Error loading config:', message)
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])
  
  // AGC: Load config on mount
  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  // AGC: Update algorithm weight
  const updateWeight = (key: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      weights: { ...prev.weights, [key]: value },
    }))
    setHasChanges(true)
  }

  // AGC: Update general setting
  const updateGeneral = (field: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      general: { ...prev.general, [field]: value },
    }))
    setHasChanges(true)
  }

  // AGC: Open crop edit modal
  const handleEditCrop = (crop: CropRequirements) => {
    setEditingCrop(crop)
    setIsModalOpen(true)
  }

  // AGC: Save edited crop
  const handleSaveCrop = (updatedCrop: CropRequirements) => {
    setConfig(prev => ({
      ...prev,
      crops: prev.crops.map(c => c.id === updatedCrop.id ? updatedCrop : c),
    }))
    setHasChanges(true)
    setIsModalOpen(false)
  }

  // AGC: Add new crop
  const handleAddCrop = () => {
    const newCrop: CropRequirements = {
      id: `crop_${Date.now()}`,
      name: 'Novo Cultivo',
      icon: '🌱',
      color: '#6b7280',
      precipitationTotal: 400,
      tempOptimalMin: 18,
      tempOptimalMax: 30,
      radiationTotal: 2000,
      projectedYield: 3.0,
      cycleDays: 120,
      growthStages: [
        { code: 'VE', name: 'Emergência', startDay: 0 },
        { code: 'V1', name: 'Vegetativo', startDay: 15 },
        { code: 'R1', name: 'Reprodutivo', startDay: 60 },
        { code: 'M', name: 'Maturação', startDay: 100 },
      ],
    }
    setEditingCrop(newCrop)
    setIsModalOpen(true)
  }

  // AGC: Update ZARC penalty
  const updatePenalty = (id: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      zarcPenalties: prev.zarcPenalties.map(p => 
        p.id === id ? { ...p, value } : p
      ),
    }))
    setHasChanges(true)
  }

  // AGC: [API] Save configuration to backend
  const handleSave = async () => {
    setSaving(true)
    setError(null)
    
    try {
      const backendUrl = getBackendUrl()
      
      // AGC: Transform crops to API format (object keyed by cropId)
      const cropsObj = config.crops.reduce((acc, crop) => {
        acc[crop.id] = {
          cropId: crop.id,
          name: crop.name,
          icon: crop.icon,
          color: crop.color,
          precipitationTotal: crop.precipitationTotal,
          tempOptimalMin: crop.tempOptimalMin,
          tempOptimalMax: crop.tempOptimalMax,
          radiationTotal: crop.radiationTotal,
          projectedYield: crop.projectedYield,
          cycleDays: crop.cycleDays,
          plantingMonths: crop.plantingMonths || [9, 10, 11, 12],
          growthStages: crop.growthStages,
          source: 'admin-panel',
        }
        return acc
      }, {} as Record<string, any>)
      
      const response = await fetch(`${backendUrl}/api/v1/admin/config/crop-requirements`, {
        method: 'PUT',
        headers: {
          'Authorization': 'Bearer admin-token',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          crops: cropsObj,
          updatedBy: 'admin-panel',
        }),
      })
      
      if (!response.ok) {
        throw new Error(`Failed to save config: ${response.statusText}`)
      }
      
      const data = await response.json()
      
      if (data.success) {
        setConfig(prev => ({
          ...prev,
          updatedAt: data.data.version,
          updatedBy: data.data.updatedBy || 'admin',
        }))
        setHasChanges(false)
        setConfigSource('firestore')
        setLastSaved(data.data.version)
        console.log('✅ AGC: [ADMIN_CONFIG] Configuration saved successfully')
        alert('Configurações salvas com sucesso!')
      } else {
        throw new Error(data.error || 'Unknown error')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      console.error('❌ AGC: [ADMIN_CONFIG] Error saving config:', message)
      setError(message)
      alert(`Erro ao salvar configurações: ${message}`)
    } finally {
      setSaving(false)
    }
  }

  // AGC: Generate formula string
  const formula = `score_total = (
  score_climatico × ${config.weights.climate.toFixed(2)} +
  score_edafico × ${config.weights.soil.toFixed(2)} +
  score_rotacao × ${config.weights.rotation.toFixed(2)} +
  score_economico × ${config.weights.economic.toFixed(2)} +
  score_temporal × ${config.weights.temporal.toFixed(2)}
)

if (score_total >= ${config.general.minScoreToRecommend}) → RECOMENDAR
if (score_total < ${config.general.minScoreToRecommend}) → DESCARTAR`

  // AGC: Group penalties by category
  const penaltiesByCategory = config.zarcPenalties.reduce<Record<string, ZarcPenalty[]>>((acc, p) => {
    const arr = acc[p.category] ?? []
    arr.push(p)
    acc[p.category] = arr
    return acc
  }, {})

  const categoryLabels: Record<string, string> = {
    risk_level: '🎯 Nível de Risco ZARC',
    temperature: '🌡️ Temperatura',
    precipitation: '💧 Precipitação',
    rotation: '🔄 Rotação',
  }

  // AGC: Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4" />
          <p className="text-slate-500">Carregando configurações...</p>
        </div>
      </div>
    )
  }

  // AGC: Save ZARC weights to backend
  const handleSaveZarc = async () => {
    setSavingZarc(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const res = await fetch(`${backendUrl}/api/v1/admin/config/zarc-weights`, {
        method: 'PUT',
        headers: { 'Authorization': 'Bearer admin-token', 'Content-Type': 'application/json' },
        body: JSON.stringify({ weights: zarcWeights, updatedBy: 'admin-panel' }),
      })
      const data = await res.json()
      if (data.success) {
        setZarcDirty(false)
        setZarcSource('admin-panel')
      } else {
        setError(data.error || 'Failed to save ZARC weights')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSavingZarc(false)
    }
  }

  // AGC: Update a single ZARC weight and rebalance others proportionally (integer math to avoid float drift)
  const updateZarcWeight = (key: keyof ZarcWeightsConfig, value: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(value)))
    const oldPct = Math.round(zarcWeights[key] * 100)
    if (clamped === oldPct) return

    const allKeys = ['precipitation', 'temperature', 'radiation', 'season'] as (keyof ZarcWeightsConfig)[]
    const otherKeys = allKeys.filter(k => k !== key)
    const otherPcts = otherKeys.map(k => Math.round(zarcWeights[k] * 100))
    const otherSum = otherPcts.reduce((s, v) => s + v, 0)
    const remaining = 100 - clamped

    const newPcts: Record<string, number> = { [key]: clamped }
    if (otherSum > 0 && remaining > 0) {
      let distributed = 0
      otherKeys.forEach((k, i) => {
        if (i < otherKeys.length - 1) {
          const share = Math.round(((otherPcts[i] ?? 0) / otherSum) * remaining)
          newPcts[k] = share
          distributed += share
        } else {
          newPcts[k] = remaining - distributed
        }
      })
    } else {
      for (const k of otherKeys) newPcts[k] = 0
    }

    const updated = {} as ZarcWeightsConfig
    for (const k of allKeys) updated[k] = (newPcts[k as string] ?? 0) / 100
    setZarcWeights(updated)
    setZarcDirty(true)
  }

  // AGC: Restore EMBRAPA defaults
  const restoreZarcDefaults = () => {
    setZarcWeights({ ...DEFAULT_ZARC_WEIGHTS })
    setZarcDirty(true)
  }

  const ZARC_LABELS: Record<string, { label: string; icon: string; color: string }> = {
    precipitation: { label: t('admin.zarc.precipitation'), icon: '💧', color: '#3b82f6' },
    temperature: { label: t('admin.zarc.temperature'), icon: '🌡️', color: '#ef4444' },
    radiation: { label: t('admin.zarc.radiation'), icon: '☀️', color: '#f59e0b' },
    season: { label: t('admin.zarc.season'), icon: '📅', color: '#8b5cf6' },
  }

  const TAB_ITEMS = [
    { id: 'crops' as const, label: t('admin.tabs.crops'), icon: '🌾' },
    { id: 'zarc' as const, label: t('admin.tabs.zarc'), icon: '⚖️' },
    { id: 'yields' as const, label: t('admin.tabs.yields'), icon: '📊' },
    { id: 'versions' as const, label: t('admin.tabs.versions'), icon: '📋' },
  ]

  return (
    <div className="space-y-4">
      {/* AGC: [AR] Per-country model config — Brasil keeps the existing UI, AR renders the new editor. */}
      <CountrySwitcher value={modelCountry} onChange={setModelCountry} />
      {modelCountry === 'AR' ? (
        <ArgentinaModelConfig />
      ) : (
    <div className="pb-24">
      {/* AGC: Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200 flex items-center gap-3">
          <span className="text-3xl">🌱</span>
          {t('admin.models.recomendacoes') || 'Modelos › Recomendações'}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('admin.models.recomendacoesDescription') || 'Configure os algoritmos de recomendação de cultivos baseados em dados EMBRAPA/ZARC'}
        </p>

        {/* AGC: Config source indicator */}
        <div className="flex items-center gap-4 mt-3">
          <span className={`text-xs px-2 py-1 rounded-full ${
            configSource === 'firestore'
              ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
              : 'bg-warning-50 dark:bg-yellow-500/15 text-warning-700 dark:text-yellow-400'
          }`}>
            {configSource === 'firestore' ? '☁️ Firestore' : '⚠️ Valores Padrão'}
          </span>
          {lastSaved && (
            <span className="text-xs text-slate-400 dark:text-slate-500">
              Última atualização: {new Date(lastSaved).toLocaleString('pt-BR')}
            </span>
          )}
          {error && (
            <span className="text-xs px-2 py-1 rounded-full bg-error-50 dark:bg-red-500/15 text-error-700 dark:text-red-400">
              ❌ {error}
            </span>
          )}
        </div>
      </div>

      {/* AGC: Tab navigation */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {TAB_ITEMS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* AGC: Tab — Crops */}
      {activeTab === 'crops' && (
        <>
          {/* AGC: Overview metrics */}
          <div className="grid grid-cols-5 gap-5 mb-8">
            <MetricCard icon="🌱" value={config.crops.length} label="Cultivos Cadastrados" color="#22c55e" />
            <MetricCard icon="⚖️" value="5" label="Fatores de Score" color="#3b82f6" />
            <MetricCard icon="🎯" value={config.general.minScoreToRecommend} label="Score Mínimo" color="#eab308" />
            <MetricCard icon="📋" value={config.general.maxRecommendations} label="Máx. Recomendações" color="#22c55e" />
            <MetricCard icon="⚠️" value={config.zarcPenalties.length} label="Penalidades ZARC" color="#f97316" />
          </div>

          {/* AGC: Algorithm Weights Section */}
          <SectionCard
            title="Pesos do Algoritmo de Recomendação"
            icon="⚖️"
            description="Distribua o peso de cada fator no cálculo do score final de recomendação (EMBRAPA)"
            collapsible
            defaultExpanded={true}
          >
            <div className="grid grid-cols-2 gap-8">
              <WeightDistribution
                weights={config.weights}
                labels={ALGO_WEIGHT_LABELS}
                colors={ALGO_COLORS}
                onChange={updateWeight}
              />

              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                  <span className="text-lg">⚙️</span> Configurações Gerais
                </h4>
                <EditableSlider
                  label="Score Mínimo para Recomendar"
                  value={config.general.minScoreToRecommend}
                  min={0}
                  max={100}
                  unit="pts"
                  onChange={(v) => updateGeneral('minScoreToRecommend', v)}
                  description="Cultivos abaixo deste score não serão recomendados"
                />
                <EditableSlider
                  label="Máximo de Recomendações"
                  value={config.general.maxRecommendations}
                  min={1}
                  max={10}
                  unit=""
                  onChange={(v) => updateGeneral('maxRecommendations', v)}
                  description="Quantidade de opções mostradas ao produtor"
                />
              </div>
            </div>

            <FormulaPreview
              title="Fórmula de Score Total (Especificação EMBRAPA)"
              formula={formula}
              bgColor="#eff6ff"
              borderColor="#bfdbfe"
              titleColor="#1e40af"
              codeColor="#60a5fa"
            />
          </SectionCard>

          {/* AGC: Crop Requirements Section */}
          <SectionCard
            title="Requisitos Climáticos por Cultivo"
            icon="🌾"
            description="Configure os parâmetros ideais para cada cultivo baseado em dados EMBRAPA"
            collapsible
            defaultExpanded={true}
          >
            <CropRequirementsTable
              crops={config.crops}
              onEdit={handleEditCrop}
            />

            <div className="mt-4 flex gap-3">
              <button
                onClick={handleAddCrop}
                className="px-4 py-2.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                ➕ Adicionar Cultivo
              </button>
              <button className="px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors flex items-center gap-2">
                📥 Importar dados EMBRAPA
              </button>
              <button className="px-4 py-2.5 bg-slate-500 text-white rounded-lg text-sm font-medium hover:bg-slate-600 transition-colors flex items-center gap-2">
                📤 Exportar JSON
              </button>
            </div>
          </SectionCard>

          {/* AGC: ZARC Penalties Section */}
          <SectionCard
            title="Penalidades ZARC"
            icon="⚠️"
            description="Ajustes de score baseados em fatores de risco climático (dados ZARC/MAPA)"
            collapsible
            defaultExpanded={true}
          >
            {/* AGC: Info banner */}
            <div className="p-4 bg-warning-50 dark:bg-yellow-500/15 rounded-xl border border-slate-200 dark:border-white/5 mb-6">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📋</span>
                <div>
                  <strong className="text-warning-700 dark:text-yellow-400">Metodologia ZARC</strong>
                  <p className="text-sm text-warning-700 dark:text-yellow-400 mt-1">
                    Penalidades aplicadas automaticamente quando condições climáticas fogem das
                    recomendações do Zoneamento Agrícola de Risco Climático (MAPA).
                  </p>
                </div>
              </div>
            </div>

            {/* AGC: Penalties by category */}
            {Object.entries(penaltiesByCategory).map(([category, penalties]) => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  {categoryLabels[category] || category}
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {penalties.map((penalty) => (
                    <div
                      key={penalty.id}
                      className="p-4 bg-error-50 dark:bg-red-500/15 rounded-xl border border-slate-200 dark:border-white/5"
                    >
                      <div className="text-xs text-error-700 dark:text-red-400 mb-2 font-medium">{penalty.label}</div>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="number"
                          value={penalty.value}
                          onChange={(e) => updatePenalty(penalty.id, Number(e.target.value))}
                          className="w-20 px-2 py-1 border border-slate-200 dark:border-white/10 rounded text-lg font-bold text-error-700 dark:text-red-400 bg-white dark:bg-admin-bg-dark"
                        />
                        <span className="text-error-700 dark:text-red-400 font-semibold">pts</span>
                      </div>
                      <p className="text-xs text-error-700 dark:text-red-400">{penalty.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </SectionCard>

          {/* AGC: Growth Stages Overview */}
          <SectionCard
            title="Estágios Fenológicos"
            icon="📈"
            description="Visão geral dos estágios de crescimento configurados por cultivo"
            collapsible
            defaultExpanded={false}
          >
            <div className="grid grid-cols-3 gap-4">
              {config.crops.map(crop => (
                <div
                  key={crop.id}
                  className="p-4 rounded-xl"
                  style={{
                    backgroundColor: `${crop.color}10`,
                    border: `2px solid ${crop.color}30`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{crop.icon}</span>
                    <div>
                      <strong className="text-slate-700 dark:text-slate-300">{crop.name}</strong>
                      <div className="text-xs text-slate-500 dark:text-slate-400">Ciclo: {crop.cycleDays} dias</div>
                    </div>
                  </div>

                  {/* AGC: Mini stage timeline */}
                  <div className="flex gap-1 mb-3">
                    {crop.growthStages.map((stage, i) => (
                      <div
                        key={i}
                        className="flex-1 h-1 rounded-full"
                        style={{ backgroundColor: crop.color }}
                        title={`${stage.code}: ${stage.name} (dia ${stage.startDay})`}
                      />
                    ))}
                  </div>

                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {crop.growthStages.length} estágios configurados
                  </div>

                  <button
                    onClick={() => handleEditCrop(crop)}
                    className="mt-2 w-full py-2 text-sm font-medium rounded-lg text-white transition-colors"
                    style={{ backgroundColor: crop.color }}
                  >
                    Ver/Editar Estágios
                  </button>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}

      {/* AGC: Tab — ZARC Weights */}
      {activeTab === 'zarc' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚖️</span>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.zarc.title')}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                {zarcSource === 'embrapa-defaults' ? 'EMBRAPA Defaults' : zarcSource === 'admin-panel' ? t('admin.zarc.customized') : zarcSource}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={restoreZarcDefaults}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('admin.zarc.restoreDefaults')}
              </button>
              {zarcDirty && (
                <button
                  onClick={handleSaveZarc}
                  disabled={savingZarc}
                  className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium disabled:opacity-50"
                >
                  {savingZarc ? t('common.saving') : t('admin.save')}
                </button>
              )}
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
            {t('admin.zarc.description')}
          </div>

          {/* AGC: Weight sliders */}
          <div className="grid grid-cols-2 gap-6">
            {Object.entries(ZARC_LABELS).map(([key, meta]) => {
              const pct = Math.round(zarcWeights[key as keyof ZarcWeightsConfig] * 100)
              return (
                <div key={key} className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span>{meta.icon}</span> {meta.label}
                    </span>
                    <span className="text-lg font-bold" style={{ color: meta.color }}>{pct}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={pct}
                    onChange={e => updateZarcWeight(key as keyof ZarcWeightsConfig, parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
                    style={{ accentColor: meta.color }}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* AGC: Total indicator */}
          {(() => {
            const total = Math.round(Object.values(zarcWeights).reduce((s, v) => s + v * 100, 0))
            return (
              <div className={`p-3 rounded-lg text-center text-sm font-medium ${
                total === 100
                  ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400'
              }`}>
                {t('admin.zarc.total')}: {total}% {total === 100 ? '✓' : `(${t('admin.zarc.mustEqual100')})`}
              </div>
            )
          })()}

          {/* AGC: Formula preview for ZARC */}
          <FormulaPreview
            title={t('admin.zarc.formulaTitle')}
            formula={`score = precip × ${zarcWeights.precipitation.toFixed(2)} + temp × ${zarcWeights.temperature.toFixed(2)} + rad × ${zarcWeights.radiation.toFixed(2)} + season × ${zarcWeights.season.toFixed(2)}`}
            bgColor="#f0fdf4"
            borderColor="#bbf7d0"
            titleColor="#166534"
            codeColor="#4ade80"
          />
        </div>
      )}

      {/* AGC: Tab — Yield Models */}
      {activeTab === 'yields' && <YieldModelsConfig />}

      {/* AGC: Tab — Versions */}
      {activeTab === 'versions' && <RecommendationVersions />}

      {/* AGC: Crop edit modal */}
      <CropEditModal
        crop={editingCrop}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingCrop(null)
        }}
        onSave={handleSaveCrop}
      />

      {/* AGC: Floating save bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-admin-card-dark border-t border-slate-200 dark:border-white/5 px-4 pt-4 pb-safe-4 flex justify-between items-center shadow-lg z-[90]">
          <div className="flex items-center gap-2 text-amber-600">
            <span className="text-lg">⚠️</span>
            <span className="text-sm font-medium">Você tem alterações não salvas</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setConfig(DEFAULT_RECOMENDACOES_CONFIG)
                setHasChanges(false)
              }}
              className="px-5 py-2.5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/[0.02] flex items-center gap-2"
            >
              ✕ Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] rounded-lg hover:bg-brand-dark dark:hover:opacity-90 flex items-center gap-2 font-medium disabled:opacity-50"
            >
              💾 {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      )}
    </div>
      )}
    </div>
  )
}

export default RecomendacoesConfig


